import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastAction } from '../hooks/useToastAction';
import {
  Table,
  type Column,
  Badge,
  Button,
  ConcertModal,
  type ConcertFormData,
  ContactOverlay,
  TodoModal,
  type TodoFormData,
  type ContextMenuItem,
  TableSkeleton,
  TableLoadingOverlay,
  Pagination,
  SearchInput,
  DateRangeFilter,
  SaveViewModal,
  EditViewModal,
  isEssentialsComplete,
  isNegotiatedComplete,
  isChecklistComplete,
} from '../components/Common';
import { bandsAPI, venuesAPI, contactsAPI, concertsAPI, concertViewsAPI } from '../api/endpoints';
import { useConcertSearch, DEFAULT_STATUS_FILTER } from '../hooks/useConcertSearch';
import { useLatestView } from '../hooks/useLatestView';
import { useOpenRequest } from '../hooks/useOpenRequest';
import {
  CLEARED_DATE_FILTERS,
  concertDateMode,
  pastDateFilters,
  upcomingDateFilters,
} from '../utils/concertDates';
import type { Concert, Band, Venue, Contact, Address, ChecklistItem, ConcertView, ConcertViewFilters } from '../types';
import clsx from 'clsx';
import { Plus, CheckSquare, Pencil, MessageSquare, Bell, Save, Settings, Music, History } from 'lucide-react';
import { format } from 'date-fns';

function ChecklistTooltip({ items }: { items: ChecklistItem[] }) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ top: rect.top - 4, left: rect.left });
  };

  const done = items.filter((i) => i.done).length;
  const total = items.length;

  return (
    <div
      ref={triggerRef}
      className="flex items-center gap-1.5 text-xs"
      onMouseEnter={show}
      onMouseLeave={() => setPos(null)}
    >
      <CheckSquare className="w-3.5 h-3.5 text-dark-text-muted" />
      <span className={done === total ? 'text-green-400' : 'text-dark-text-secondary'}>
        {done}/{total}
      </span>
      {pos && createPortal(
        <div
          className="fixed z-9999 min-w-[180px] max-w-[260px] bg-dark-surface border border-dark-border rounded-lg shadow-xl p-2 space-y-1"
          style={{ top: pos.top, left: pos.left, transform: 'translateY(-100%)' }}
        >
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs">
              <span className={item.done ? 'text-green-400' : 'text-dark-text-muted'}>
                {item.done ? '✓' : '○'}
              </span>
              <span className={item.done ? 'text-dark-text-secondary line-through' : 'text-dark-text-primary'}>
                {item.label}
              </span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function DoneCheckmark({ complete, label, color }: { complete: boolean; label: string; color?: string }) {
  return (
    <span title={label}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`w-3.5 h-3.5 ${complete ? (color || 'text-green-400') : 'text-dark-text-muted/30'}`}
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
    </span>
  );
}

function ChecklistCheckmark({ items }: { items: ChecklistItem[] }) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const complete = isChecklistComplete(items);

  const show = () => {
    if (items.length === 0) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ top: rect.top - 4, left: rect.left });
  };

  return (
    <span
      ref={triggerRef}
      title={items.length === 0 ? 'Todo' : undefined}
      onMouseEnter={show}
      onMouseLeave={() => setPos(null)}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`w-3.5 h-3.5 ${complete ? 'text-green-400' : 'text-dark-text-muted/30'}`}
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      {pos && items.length > 0 && createPortal(
        <div
          className="fixed z-9999 min-w-[180px] max-w-[260px] bg-dark-surface border border-dark-border rounded-lg shadow-xl p-2 space-y-1"
          style={{ top: pos.top, left: pos.left, transform: 'translateY(-100%)' }}
        >
          <div className="text-[10px] font-medium text-dark-text-muted mb-1">Todo</div>
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs">
              <span className={item.done ? 'text-green-400' : 'text-dark-text-muted'}>
                {item.done ? '✓' : '○'}
              </span>
              <span className={item.done ? 'text-dark-text-secondary line-through' : 'text-dark-text-primary'}>
                {item.label}
              </span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
}

function DoneIndicator({ row }: { row: ConcertRow }) {
  const essentialsDone = isEssentialsComplete(row);
  const isCanceled = row.status === 'canceled';
  return (
    <div className="flex items-center gap-0.5">
      <DoneCheckmark complete={essentialsDone || isCanceled} label="Essentials" color={isCanceled ? 'text-red-400' : undefined} />
      <DoneCheckmark complete={isNegotiatedComplete(row)} label="Negotiated" />
      <ChecklistCheckmark items={row.checklist} />
    </div>
  );
}

const STATUS_BADGE: Record<string, { label: string; variant: 'success' | 'error' | 'info' }> = {
  done: { label: 'Done', variant: 'success' },
  canceled: { label: 'Canceled', variant: 'error' },
  in_discussion: { label: 'In Discussion', variant: 'info' },
};

// Module-level so useQueries' combine keeps a stable identity and only re-runs when a result changes
function loadedVenues(results: { data?: Venue }[]): Venue[] {
  return results.map((r) => r.data).filter((v): v is Venue => v !== undefined);
}

const STATUS_FILTER_VALUES = [
  { value: 'in_discussion', label: 'In Discussion' },
  { value: 'done', label: 'Done' },
  { value: 'canceled', label: 'Canceled' },
];

const CHECKLIST_FILTER_VALUES = [
  { value: 'complete', label: 'Complete' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'empty', label: 'No Checklist' },
];

const COLUMN_LABELS: Record<string, string> = {
  done: 'Done',
  status: 'Status',
  bandName: 'Band',
  date: 'Date',
  venueName: 'Venue',
  venueAddress: 'Address',
  totalAmount: 'Amount',
  benefits: 'Benefits',
  times: 'Times',
  duration: 'Duration',
  backline: 'Backline',
  technique: 'Technique',
  otherInfo: 'Other Info',
  adminContact: 'Admin',
  checklist: 'Checklist',
};

const COLUMN_FILTER_PARAMS: Record<string, string[]> = {
  done: [],
  status: ['status'],
  bandName: ['band_id'],
  venueName: ['venue_id'],
  venueAddress: [],
  date: ['date_after', 'date_before'],
  totalAmount: [],
  benefits: [],
  times: [],
  duration: [],
  backline: [],
  technique: [],
  otherInfo: [],
  adminContact: [],
  checklist: ['checklist'],
};

interface ConcertRow extends Concert {
  venueName: string;
  venueAddress?: Address;
  bandName: string;
}

export function ConcertsPage() {
  const queryClient = useQueryClient();
  const { run } = useToastAction();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingConcert, setEditingConcert] = useState<Concert | null>(null);

  // Contact overlay state
  const [isContactOverlayOpen, setIsContactOverlayOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [selectedVenueContacts, setSelectedVenueContacts] = useState<Contact[]>([]);
  const openRequest = useOpenRequest();

  // Todo modal state
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [todoVenue, setTodoVenue] = useState<Venue | null>(null);
  const [todoConcertRow, setTodoConcertRow] = useState<ConcertRow | null>(null);
  const [todoContacts, setTodoContacts] = useState<Contact[]>([]);
  const todoVenueIdRef = useRef<string | null>(null);

  // View state
  const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
  const [editingView, setEditingView] = useState<ConcertView | null>(null);
  const [confirmedViewId, setConfirmedViewId] = useState<string | null>(null);
  const [pendingViewId, setPendingViewId] = useState<string | null>(null);

  // Column settings
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('concerts-hidden-columns');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const columnSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const columnSettingsDropdownRef = useRef<HTMLDivElement>(null);
  const [columnSettingsPosition, setColumnSettingsPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isColumnSettingsPositioned, setIsColumnSettingsPositioned] = useState(false);

  // Use server-side search hook
  const {
    concerts,
    pagination,
    isLoading: isLoadingConcerts,
    isFetching,
    params,
    setSearch: applySearch,
    setFilter: applyFilter,
    setFilters: applyFilters,
    setSort: applySort,
    setPage,
    resetFilters,
    setAllParams,
    refetch: refetchConcerts,
  } = useConcertSearch();

  // Which of the Upcoming / Past shortcuts the current range spells out, if either.
  const dateMode = useMemo(
    () => concertDateMode(params.date_after, params.date_before),
    [params.date_after, params.date_before],
  );

  // Fetch supporting data
  const { data: bands = [] } = useQuery({
    queryKey: ['bands'],
    queryFn: async () => {
      const response = await bandsAPI.list();
      return response.data.items;
    },
    staleTime: 60000,
  });

  // Fetch only the venues the listed concerts reference, one cached query per venue. A user can
  // own tens of thousands of venues, so a bulk list would both be slow and miss most of them.
  const concertVenueIds = useMemo(
    () => [...new Set(concerts.map((c) => c.venue_id).filter(Boolean))],
    [concerts],
  );

  const allVenues = useQueries({
    queries: concertVenueIds.map((id) => ({
      queryKey: ['concert-venue', id],
      queryFn: async () => (await venuesAPI.getById(id)).data,
      staleTime: 60000,
    })),
    combine: loadedVenues,
  });

  // Fetch concert views
  const { data: concertViews = [], refetch: refetchViews } = useQuery({
    queryKey: ['concert-views'],
    queryFn: async () => {
      const response = await concertViewsAPI.list();
      return response.data;
    },
    staleTime: 60000,
  });

  // Fetch total concert count for the title
  const { data: totalConcertCount } = useQuery({
    queryKey: ['concerts-total-count'],
    queryFn: async () => {
      const response = await concertsAPI.list({ page_size: 1 });
      return response.data.total;
    },
    staleTime: 60000,
  });

  // Build lookup maps
  const bandMap = useMemo(() => {
    const m = new Map<string, Band>();
    bands.forEach((b) => m.set(b._id, b));
    return m;
  }, [bands]);

  const venueMap = useMemo(() => {
    const m = new Map<string, Venue>();
    allVenues.forEach((v) => m.set(v._id, v));
    return m;
  }, [allVenues]);

  // Build enriched rows
  const concertRows: ConcertRow[] = useMemo(() => {
    return concerts.map((c) => {
      const venue = venueMap.get(c.venue_id);
      const band = bandMap.get(c.band_id);
      return {
        ...c,
        venueName: venue?.name || 'Unknown venue',
        venueAddress: venue?.address || c.location,
        bandName: band?.name || 'Unknown band',
      };
    });
  }, [concerts, bandMap, venueMap]);

  // Build venue filter values from venues referenced by user's concerts
  const venueFilterValues = useMemo(() => {
    const venueIds = new Set(concerts.map((c) => c.venue_id));
    return allVenues
      .filter((v) => venueIds.has(v._id))
      .map((v) => ({ value: v._id, label: v.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [concerts, allVenues]);

  // --- View management (same pattern as VenuesPage) ---
  const getCurrentFilters = useCallback((): ConcertViewFilters => {
    const filters = Object.fromEntries(
      Object.entries(params).filter(([k, v]) => v !== undefined && k !== 'page' && k !== 'page_size')
    ) as ConcertViewFilters;
    if (hiddenColumns.length > 0) {
      filters.hidden_columns = hiddenColumns;
    }
    return filters;
  }, [params, hiddenColumns]);

  const filtersMatch = useCallback((a: ConcertViewFilters, b: ConcertViewFilters) => {
    const aRecord = a as Record<string, unknown>;
    const bRecord = b as Record<string, unknown>;
    const allKeys = new Set([...Object.keys(aRecord), ...Object.keys(bRecord)]);
    for (const key of allKeys) {
      if (key === 'hidden_columns') continue;
      if ((aRecord[key] || undefined) !== (bRecord[key] || undefined)) return false;
    }
    const aHidden = a.hidden_columns || [];
    const bHidden = b.hidden_columns || [];
    if (aHidden.length !== bHidden.length) return false;
    const aSorted = [...aHidden].sort();
    const bSorted = [...bHidden].sort();
    return aSorted.every((v, i) => v === bSorted[i]);
  }, []);

  // The reserved "Latest" view: rewritten on every filter the user picks by hand, so the state
  // they last filtered into is always one chip away. See docs/LATEST_VIEW.md.
  const latestView = useMemo(() => concertViews.find(v => v.is_latest) ?? null, [concertViews]);
  const namedViews = useMemo(() => concertViews.filter(v => !v.is_latest), [concertViews]);

  const saveLatestView = useCallback(async (filters: ConcertViewFilters) => {
    const response = await concertViewsAPI.saveLatest(filters);
    // Patch the cache instead of refetching: the one chip that changed is in the response.
    queryClient.setQueryData<ConcertView[]>(['concert-views'], (old) =>
      old ? [...old.filter(v => v._id !== response.data._id), response.data] : old
    );
  }, [queryClient]);

  const { markChange: markFilterChange, cancelPendingChange: cancelPendingLatest } = useLatestView({
    filters: getCurrentFilters(),
    latestFilters: latestView?.filters ?? null,
    save: saveLatestView,
  });

  // Every filter the user picks by hand goes through one of these wrappers, and that is what tells
  // the "Latest" view to follow. The page's own filter writes — applying a view, Reset to All —
  // deliberately do not, or every click on a view would overwrite Latest with that view.
  const setSearch = useCallback((q: string) => {
    markFilterChange('search');
    applySearch(q);
  }, [markFilterChange, applySearch]);
  const setFilter = useCallback((key: string, value: string | undefined) => {
    markFilterChange();
    applyFilter(key, value);
  }, [markFilterChange, applyFilter]);
  const setFilters = useCallback((filters: Record<string, string | undefined>) => {
    markFilterChange();
    applyFilters(filters);
  }, [markFilterChange, applyFilters]);
  const setSort = useCallback((sortBy: string, sortOrder: 'asc' | 'desc' | null) => {
    markFilterChange();
    applySort(sortBy, sortOrder);
  }, [markFilterChange, applySort]);

  const activeViewId = useMemo(() => {
    const currentFilters = getCurrentFilters();
    if (confirmedViewId) {
      const viewFilters = concertViews.find(v => v._id === confirmedViewId)?.filters;
      if (viewFilters && filtersMatch(currentFilters, viewFilters)) {
        return confirmedViewId;
      }
    }
    if (pendingViewId) {
      const viewFilters = concertViews.find(v => v._id === pendingViewId)?.filters;
      if (viewFilters && filtersMatch(currentFilters, viewFilters)) {
        return pendingViewId;
      }
    }
    return null;
  }, [confirmedViewId, pendingViewId, getCurrentFilters, concertViews, filtersMatch]);

  const isUnsavedView = useMemo(() => {
    const currentFilters = getCurrentFilters();
    // The page's own defaults — the "upcoming" bound and the live statuses — are not filters the
    // user picked, so on their own they do not turn the page into an unsaved view worth nagging
    // about.
    const isDefaultDates =
      concertDateMode(currentFilters.date_after, currentFilters.date_before) === 'upcoming';
    const isDefaultStatus = currentFilters.status === DEFAULT_STATUS_FILTER;
    const hasActiveFilters = Object.entries(currentFilters).some(
      ([key, value]) =>
        value !== undefined &&
        !(isDefaultDates && key === 'date_after') &&
        !(isDefaultStatus && key === 'status'),
    );
    const effectiveViewId = activeViewId || pendingViewId;
    // "Latest" follows the filters around on its own, so it is not a view the user saved: the nag
    // is about the named views. See docs/LATEST_VIEW.md.
    const hasNamedView = !!effectiveViewId && effectiveViewId !== latestView?._id;
    return !hasNamedView && hasActiveFilters;
  }, [getCurrentFilters, activeViewId, pendingViewId, latestView]);

  const saveViewButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isUnsavedView) {
      saveViewButtonRef.current?.focus();
    }
  }, [isUnsavedView]);

  const handleApplyView = (view: ConcertView) => {
    cancelPendingLatest();
    setPendingViewId(view._id);
    setConfirmedViewId(null);
    const { hidden_columns, ...filterParams } = view.filters;
    setAllParams(filterParams as Record<string, string | undefined>);
    const newHidden = hidden_columns || [];
    setHiddenColumns(newHidden);
    localStorage.setItem('concerts-hidden-columns', JSON.stringify(newHidden));
  };

  const handleResetToAll = () => {
    cancelPendingLatest();
    setConfirmedViewId(null);
    setPendingViewId(null);
    resetFilters();
    setHiddenColumns([]);
    localStorage.setItem('concerts-hidden-columns', JSON.stringify([]));
  };

  const handleSaveView = async (name: string, filters: ConcertViewFilters) => {
    const filtersWithColumns: ConcertViewFilters = {
      ...filters,
      hidden_columns: hiddenColumns.length > 0 ? hiddenColumns : undefined,
    };
    const response = await concertViewsAPI.create({ name, filters: filtersWithColumns });
    await refetchViews();
    setConfirmedViewId(response.data._id);
    setPendingViewId(null);
  };

  const handleUpdateViewFilters = async (id: string, filters: ConcertViewFilters) => {
    const filtersWithColumns: ConcertViewFilters = {
      ...filters,
      hidden_columns: hiddenColumns.length > 0 ? hiddenColumns : undefined,
    };
    await concertViewsAPI.update(id, { filters: filtersWithColumns });
    await refetchViews();
    setConfirmedViewId(id);
    setPendingViewId(null);
  };

  const handleRenameView = async (id: string, newName: string) => {
    await concertViewsAPI.update(id, { name: newName });
    await refetchViews();
  };

  const handleDeleteView = async (id: string) => {
    await concertViewsAPI.delete(id);
    await refetchViews();
    if (confirmedViewId === id) setConfirmedViewId(null);
    if (pendingViewId === id) setPendingViewId(null);
  };

  // Drag-and-drop reordering
  const [draggedViewId, setDraggedViewId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{ viewId: string; side: 'left' | 'right' } | null>(null);

  const handleDragStart = (e: React.DragEvent, viewId: string) => {
    setDraggedViewId(viewId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', viewId);
  };

  const handleDragOver = (e: React.DragEvent, viewId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (viewId === draggedViewId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const side = e.clientX < midX ? 'left' : 'right';
    setDropIndicator({ viewId, side });
  };

  const handleDragLeave = () => {
    setDropIndicator(null);
  };

  const getInsertIndex = (targetViewId: string, side: 'left' | 'right') => {
    const currentOrder = namedViews.map(v => v._id);
    const dragIdx = currentOrder.indexOf(draggedViewId!);
    let dropIdx = currentOrder.indexOf(targetViewId);
    if (side === 'right') dropIdx++;
    if (dragIdx < dropIdx) dropIdx--;
    return dropIdx;
  };

  const handleDrop = async (e: React.DragEvent, targetViewId: string) => {
    e.preventDefault();
    const side = dropIndicator?.viewId === targetViewId ? dropIndicator.side : 'left';
    setDropIndicator(null);
    if (!draggedViewId || draggedViewId === targetViewId) {
      setDraggedViewId(null);
      return;
    }
    const currentOrder = namedViews.map(v => v._id);
    const dragIdx = currentOrder.indexOf(draggedViewId);
    if (dragIdx === -1) return;
    currentOrder.splice(dragIdx, 1);
    const insertIdx = getInsertIndex(targetViewId, side);
    currentOrder.splice(insertIdx, 0, draggedViewId);
    setDraggedViewId(null);
    queryClient.setQueryData(['concert-views'], (old: ConcertView[] | undefined) => {
      if (!old) return old;
      const map = new Map(old.map(v => [v._id, v]));
      const reordered = currentOrder.map((id, i) => ({ ...map.get(id)!, display_order: i }));
      // Latest is not in the dragged order — keep it, or the chip would vanish until the refetch.
      const latest = old.find(v => v.is_latest);
      return latest ? [...reordered, latest] : reordered;
    });
    await concertViewsAPI.reorder(currentOrder);
    await refetchViews();
  };

  const handleDragEnd = () => {
    setDraggedViewId(null);
    setDropIndicator(null);
  };

  // Column settings dropdown
  const openColumnSettings = useCallback(() => {
    if (!columnSettingsButtonRef.current) return;
    const rect = columnSettingsButtonRef.current.getBoundingClientRect();
    const dropdownWidth = 220;
    const viewportWidth = window.innerWidth;
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth - 8) {
      left = Math.max(8, rect.right - dropdownWidth);
    }
    const top = rect.bottom + 4;
    setColumnSettingsPosition({ top, left });
    setIsColumnSettingsPositioned(true);
    setIsColumnSettingsOpen(true);
  }, []);

  useEffect(() => {
    if (!isColumnSettingsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        columnSettingsButtonRef.current && !columnSettingsButtonRef.current.contains(target) &&
        columnSettingsDropdownRef.current && !columnSettingsDropdownRef.current.contains(target)
      ) {
        setIsColumnSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isColumnSettingsOpen]);

  const columnHasActiveFilter = useCallback((columnKey: string): boolean => {
    const filterParams = COLUMN_FILTER_PARAMS[columnKey];
    if (!filterParams) return false;
    return filterParams.some(param => {
      const value = params[param as keyof typeof params];
      return value !== undefined && value !== '';
    });
  }, [params]);

  const handleToggleColumnVisibility = useCallback((columnKey: string) => {
    if (columnHasActiveFilter(columnKey)) return;
    markFilterChange();
    setHiddenColumns(prev => {
      const next = prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey];
      localStorage.setItem('concerts-hidden-columns', JSON.stringify(next));
      return next;
    });
  }, [columnHasActiveFilter, markFilterChange]);

  // Handlers
  const buildConcertPayload = (data: ConcertFormData) => ({
    // null (not undefined) so clearing the date on edit unsets it server-side
    date: data.date ? new Date(data.date).toISOString() : null,
    band_id: data.band_id,
    venue_id: data.venue_id,
    status: data.status,
    total_amount: data.total_amount || undefined,
    benefits: data.benefits || undefined,
    times: data.times || undefined,
    duration: data.duration || undefined,
    backline: data.backline || undefined,
    technique: data.technique || undefined,
    other_info: data.other_info || undefined,
    admin_contact: data.admin_contact || undefined,
    checklist: data.checklist.length > 0 ? data.checklist : undefined,
  });

  const handleCreate = (data: ConcertFormData): void => {
    setIsModalOpen(false);
    setEditingConcert(null);
    run(
      () => concertsAPI.create(buildConcertPayload(data)).then(() => {
        refetchConcerts();
        queryClient.invalidateQueries({ queryKey: ['concerts-total-count'] });
      }),
      { loading: 'Creating concert...', success: 'Concert created' },
    );
  };

  const handleUpdate = (data: ConcertFormData): void => {
    if (!editingConcert) return;
    const id = editingConcert._id;
    setIsModalOpen(false);
    setEditingConcert(null);
    run(
      () => concertsAPI.update(id, buildConcertPayload(data)).then(() => refetchConcerts()),
      { loading: 'Updating concert...', success: 'Concert updated' },
    );
  };

  const handleDelete = (): void => {
    if (!editingConcert) return;
    const id = editingConcert._id;
    setIsModalOpen(false);
    setEditingConcert(null);
    run(
      () => concertsAPI.delete(id).then(() => {
        refetchConcerts();
        queryClient.invalidateQueries({ queryKey: ['concerts-total-count'] });
      }),
      { loading: 'Deleting concert...', success: 'Concert deleted' },
    );
  };

  const handleRowClick = (row: ConcertRow) => {
    setEditingConcert(row);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  /** Fetch a venue's contacts into the overlay already on screen.
   *
   *  `token` retires the fetch if the user has closed the overlay or moved on to another venue
   *  meanwhile: a contact carries its mail bodies, about a megabyte each, so this runs for
   *  seconds on a throttled connection. */
  const loadOverlayContacts = async (venue: Venue, token: number) => {
    const contactIds = venue.contact_ids || [];
    if (contactIds.length === 0) return;
    try {
      const res = await contactsAPI.batchGet(contactIds);
      if (!openRequest.isCurrent(token)) return;
      setSelectedVenueContacts(res.data);
    } catch (err) {
      console.error('Failed to fetch contacts for overlay:', err);
    }
  };

  /** Open the contact overlay on a venue. The overlay goes up straight away and its contacts
   *  land after, so a slow fetch never holds the click.
   *
   *  The id is resolved against the listed concerts' venues first, and fetched when it is not
   *  among them — the concert modal's contact button hands over whichever venue its search field
   *  currently holds, which need not be on the page. */
  const openVenueContacts = async (venueId: string) => {
    const token = openRequest.begin();
    const cached = venueMap.get(venueId);
    if (cached) {
      setSelectedVenue(cached);
      setSelectedVenueContacts([]);
      setIsContactOverlayOpen(true);
      await loadOverlayContacts(cached, token);
      return;
    }
    try {
      const res = await venuesAPI.getById(venueId);
      if (!openRequest.isCurrent(token)) return;
      queryClient.setQueryData(['concert-venue', venueId], res.data);
      setSelectedVenue(res.data);
      setSelectedVenueContacts([]);
      setIsContactOverlayOpen(true);
      await loadOverlayContacts(res.data, token);
    } catch (err) {
      console.error('Failed to load venue for overlay:', err);
    }
  };

  /** The overlay's `onRefreshContacts` / `onRefreshVenues`: re-read the venue so the rows behind
   *  it and the overlay's own header follow the edit.
   *
   *  `selectedVenueContacts` is deliberately left alone. The overlay keeps the contact list in
   *  its own state and maintains it across adds, edits and deletes; handing it a fresh array
   *  re-runs the effect that reseeds that state, which resets the forms and re-picks the selected
   *  contact under the user. Nor does this reopen anything — the overlay asks for a refresh at the
   *  end of actions that close it, and a late fetch must not put it back on screen. */
  const refreshOverlayVenue = async () => {
    const venueId = selectedVenue?._id;
    refetchConcerts();
    if (!venueId) return;
    try {
      const res = await venuesAPI.getById(venueId);
      queryClient.setQueryData(['concert-venue', venueId], res.data);
      setSelectedVenue((current) => (current && current._id === venueId ? res.data : current));
    } catch (err) {
      console.error('Failed to refresh venue for overlay:', err);
    }
  };

  const handleOpenContact = (row: ConcertRow) => {
    void openVenueContacts(row.venue_id);
  };

  const handleOpenTodo = (row: ConcertRow) => {
    const venue = venueMap.get(row.venue_id);
    if (!venue) return;
    setTodoVenue(venue);
    setTodoConcertRow(row);
    setTodoContacts([]);
    todoVenueIdRef.current = venue._id;
    setIsTodoModalOpen(true);
    const contactIds = venue.contact_ids || [];
    if (contactIds.length === 0) return;
    // Identity fields only: the modal just lists the names the todo can be attached to.
    contactsAPI.batchGet(contactIds, { summary: true })
      .then((res) => {
        if (todoVenueIdRef.current !== venue._id) return;
        setTodoContacts(res.data);
      })
      .catch((err) => console.error('Failed to fetch contacts for todo modal:', err));
  };

  const handleCreateTodo = (data: TodoFormData): void => {
    if (!todoVenue) return;
    const venueId = todoVenue._id;
    setIsTodoModalOpen(false);
    setTodoVenue(null);
    run(
      () => venuesAPI.addTodo(venueId, data).then(() =>
        queryClient.invalidateQueries({ queryKey: ['todos'] })
      ),
      { loading: 'Creating todo...', success: 'Todo created' },
    );
  };

  const getRowContextMenu = (row: ConcertRow): ContextMenuItem[] => [
    {
      label: 'Edit Concert',
      icon: <Pencil className="w-4 h-4" />,
      onClick: () => handleRowClick(row),
    },
    {
      label: 'Contact Venue',
      icon: <MessageSquare className="w-4 h-4" />,
      onClick: () => handleOpenContact(row),
    },
    {
      label: 'Add Todo',
      icon: <Bell className="w-4 h-4" />,
      onClick: () => handleOpenTodo(row),
    },
  ];

  const renderAddress = (address?: Address) => {
    if (!address) return <span className="text-dark-text-muted">-</span>;
    const hasStructured = address.city || address.administrative_region_1 || address.administrative_region_2 || address.country;
    if (!hasStructured && address.unparsed_address) {
      return <div className="text-xs text-dark-text-secondary leading-tight">{address.unparsed_address}</div>;
    }
    if (!hasStructured) return <span className="text-dark-text-muted">-</span>;
    return (
      <div className="flex flex-col text-xs leading-tight">
        {address.city && <span className="text-dark-text-primary">{address.city}</span>}
        {address.administrative_region_1 && <span className="text-dark-text-secondary">{address.administrative_region_1}</span>}
        {address.country && <span className="text-dark-text-muted">{address.country}</span>}
      </div>
    );
  };

  // Columns
  const visibleColumns: Column<ConcertRow>[] = useMemo(() => {
    const columns: Column<ConcertRow>[] = [
      {
        key: 'done',
        label: 'Done',
        width: '70px',
        render: (row) => <DoneIndicator row={row} />,
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: '130px',
        filterable: true,
        filterConfig: {
          values: STATUS_FILTER_VALUES,
          selectedValues: params.status ? params.status.split(',') : [],
          onChange: (selected) => setFilter('status', selected.length > 0 ? selected.join(',') : undefined),
        },
        render: (row) => {
          const badge = STATUS_BADGE[row.status];
          return badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : row.status;
        },
      },
      {
        key: 'bandName',
        label: 'Band',
        sortable: false,
        width: '140px',
        filterable: true,
        filterConfig: {
          values: bands.map((b) => ({ value: b._id, label: b.name })),
          selectedValues: params.band_id ? params.band_id.split(',') : [],
          onChange: (selected) => setFilter('band_id', selected.length > 0 ? selected.join(',') : undefined),
        },
        render: (row) => (
          <span className="text-dark-text-secondary text-sm">{row.bandName}</span>
        ),
      },
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        width: '140px',
        filterable: true,
        renderFilter: () => (
          <DateRangeFilter
            fromDate={params.date_after}
            toDate={params.date_before}
            onFromChange={(val) => setFilter('date_after', val)}
            onToChange={(val) => setFilter('date_before', val)}
            onClear={() => setFilters({ ...CLEARED_DATE_FILTERS })}
            modes={[
              {
                label: 'Upcoming',
                active: dateMode === 'upcoming',
                onSelect: () =>
                  setFilters(dateMode === 'upcoming' ? { ...CLEARED_DATE_FILTERS } : upcomingDateFilters()),
              },
              {
                label: 'Past',
                active: dateMode === 'past',
                onSelect: () =>
                  setFilters(dateMode === 'past' ? { ...CLEARED_DATE_FILTERS } : pastDateFilters()),
              },
            ]}
          />
        ),
        render: (row) => {
          if (!row.date) return <span className="text-dark-text-muted">-</span>;
          return (
            <span className="text-xs text-dark-text-primary font-medium">
              {format(new Date(row.date), 'dd MMM yyyy')}
            </span>
          );
        },
      },
      {
        key: 'venueName',
        label: 'Venue',
        sortable: false,
        width: '180px',
        filterable: true,
        filterConfig: {
          values: venueFilterValues,
          selectedValues: params.venue_id ? params.venue_id.split(',') : [],
          onChange: (selected) => setFilter('venue_id', selected.length > 0 ? selected.join(',') : undefined),
        },
        render: (row) => (
          <span className="text-dark-text-primary text-sm font-medium">{row.venueName}</span>
        ),
      },
      {
        key: 'venueAddress',
        label: 'Address',
        width: '160px',
        render: (row) => renderAddress(row.venueAddress),
      },
      {
        key: 'totalAmount',
        label: 'Amount',
        width: '120px',
        render: (row) => {
          if (!row.total_amount) return <span className="text-dark-text-muted">-</span>;
          return <span className="text-dark-text-secondary text-xs line-clamp-2">{row.total_amount}</span>;
        },
      },
      {
        key: 'benefits',
        label: 'Benefits',
        width: '140px',
        render: (row) => {
          if (!row.benefits) return <span className="text-dark-text-muted">-</span>;
          return <span className="text-dark-text-secondary text-xs line-clamp-2">{row.benefits}</span>;
        },
      },
      {
        key: 'times',
        label: 'Times',
        width: '140px',
        render: (row) => {
          if (!row.times) return <span className="text-dark-text-muted">-</span>;
          return <span className="text-dark-text-secondary text-xs line-clamp-2">{row.times}</span>;
        },
      },
      {
        key: 'duration',
        label: 'Duration',
        width: '100px',
        render: (row) => {
          if (!row.duration) return <span className="text-dark-text-muted">-</span>;
          return <span className="text-dark-text-secondary text-xs line-clamp-2">{row.duration}</span>;
        },
      },
      {
        key: 'backline',
        label: 'Backline',
        width: '140px',
        render: (row) => {
          if (!row.backline) return <span className="text-dark-text-muted">-</span>;
          return <span className="text-dark-text-secondary text-xs line-clamp-2">{row.backline}</span>;
        },
      },
      {
        key: 'technique',
        label: 'Technique',
        width: '140px',
        render: (row) => {
          if (!row.technique) return <span className="text-dark-text-muted">-</span>;
          return <span className="text-dark-text-secondary text-xs line-clamp-2">{row.technique}</span>;
        },
      },
      {
        key: 'otherInfo',
        label: 'Other Info',
        width: '160px',
        render: (row) => {
          if (!row.other_info) return <span className="text-dark-text-muted">-</span>;
          return <span className="text-dark-text-secondary text-xs line-clamp-2">{row.other_info}</span>;
        },
      },
      {
        key: 'adminContact',
        label: 'Admin',
        width: '140px',
        render: (row) => {
          if (!row.admin_contact) return <span className="text-dark-text-muted">-</span>;
          return <span className="text-dark-text-secondary text-xs line-clamp-2">{row.admin_contact}</span>;
        },
      },
      {
        key: 'checklist',
        label: 'Checklist',
        width: '100px',
        filterable: true,
        filterConfig: {
          values: CHECKLIST_FILTER_VALUES,
          selectedValues: params.checklist ? params.checklist.split(',') : [],
          onChange: (selected) => setFilter('checklist', selected.length > 0 ? selected.join(',') : undefined),
        },
        render: (row) => {
          if (!row.checklist || row.checklist.length === 0) return <span className="text-dark-text-muted">-</span>;
          return <ChecklistTooltip items={row.checklist} />;
        },
      },
    ];

    return columns.filter(col => !hiddenColumns.includes(col.key));
  }, [hiddenColumns, params, dateMode, bands, venueFilterValues, setFilter, setFilters]);

  // Sort config for Table component
  const tableSortConfig = params.sort_by
    ? { key: params.sort_by, direction: (params.sort_order || 'desc') as 'asc' | 'desc' }
    : null;

  const isLoading = isLoadingConcerts;

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-2xl) mx-auto w-full p-3 sm:p-4 md:p-6 flex-1 flex flex-col">
        <div className="mb-6 animate-slideDown flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-dark-text-primary mb-2 tracking-tight flex items-center gap-2">
              <Music className="w-6 h-6" />
              Concerts{totalConcertCount !== undefined && ` (${totalConcertCount})`}
            </h1>
            <p className="text-sm text-dark-text-secondary">
              Manage your upcoming and past concerts
            </p>
          </div>
          <div className="flex items-center gap-2 animate-scaleIn">
            <Button
              variant="primary"
              onClick={() => {
                setModalMode('create');
                setEditingConcert(null);
                setIsModalOpen(true);
              }}
              size="sm"
            >
              <Plus className="w-4 h-4" />
              New Concert
            </Button>
          </div>
        </div>

        <div className="flex-1 card animate-scaleIn flex flex-col min-h-0">
          {/* Concert Views Bar */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {(() => {
              const effectiveViewId = activeViewId || pendingViewId;
              const isAllActive = !effectiveViewId && !isUnsavedView;
              // Lit when it is what the user is looking at: either they clicked it, or they
              // filtered their way into exactly what it holds with no named view applied. One
              // chip at a time — under an applied view, that view is the one lit.
              const isLatestActive = !!latestView && (
                effectiveViewId === latestView._id ||
                (!effectiveViewId && filtersMatch(getCurrentFilters(), latestView.filters))
              );
              return (
                <>
                  <button
                    onClick={handleResetToAll}
                    className={clsx(
                      'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors',
                      isAllActive
                        ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                        : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                    )}
                  >
                    All
                  </button>
                  {namedViews.map((view) => (
                    <div
                      key={view._id}
                      className={clsx(
                        'group relative flex items-center transition-all',
                        draggedViewId === view._id && 'opacity-40',
                      )}
                      draggable
                      onDragStart={(e) => handleDragStart(e, view._id)}
                      onDragOver={(e) => handleDragOver(e, view._id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, view._id)}
                      onDragEnd={handleDragEnd}
                    >
                      {dropIndicator?.viewId === view._id && dropIndicator.side === 'left' && (
                        <div className="absolute left-0 top-0.5 bottom-0.5 w-0.5 bg-primary-400 rounded-full -translate-x-1.5 z-10" />
                      )}
                      <button
                        onClick={() => handleApplyView(view)}
                        className={clsx(
                          'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors',
                          (activeViewId === view._id || pendingViewId === view._id)
                            ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                            : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                        )}
                      >
                        {view.name}
                      </button>
                      {dropIndicator?.viewId === view._id && dropIndicator.side === 'right' && (
                        <div className="absolute right-0 top-0.5 bottom-0.5 w-0.5 bg-primary-400 rounded-full translate-x-1.5 z-10" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingView(view);
                        }}
                        className="absolute -right-1 -top-1 hidden group-hover:flex items-center justify-center w-4 h-4 rounded-full bg-dark-elevated border border-dark-border text-dark-text-muted hover:text-primary-400 hover:border-primary-700/50 transition-colors"
                        title="Edit view"
                      >
                        <Pencil className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                  {latestView && (
                    <button
                      onClick={() => handleApplyView(latestView)}
                      title="The filters you last picked, saved automatically"
                      className={clsx(
                        'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors flex items-center gap-1',
                        isLatestActive
                          ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                          : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                      )}
                    >
                      <History className="w-3 h-3" />
                      {latestView.name}
                    </button>
                  )}
                  <button
                    ref={saveViewButtonRef}
                    onClick={() => setIsSaveViewModalOpen(true)}
                    className={clsx(
                      'px-3 py-1.5 rounded-sm text-xs font-medium transition-colors flex items-center gap-1',
                      isUnsavedView
                        ? 'bg-primary-900/30 text-primary-400 border border-dashed border-primary-700/50'
                        : 'bg-dark-elevated text-dark-text-muted border border-dashed border-dark-border hover:border-primary-700/50 hover:text-primary-400'
                    )}
                  >
                    <Save className="w-3 h-3" />
                    Save current view
                  </button>
                </>
              );
            })()}
          </div>

          {/* Search and top pagination */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4 items-center">
            <SearchInput
              value={params.q || ''}
              onChange={setSearch}
              placeholder="Search concerts..."
              className="flex-1 max-w-md"
            />

            {/* Column visibility settings */}
            <button
              ref={columnSettingsButtonRef}
              onClick={() => isColumnSettingsOpen ? setIsColumnSettingsOpen(false) : openColumnSettings()}
              title="Column settings"
              className={clsx(
                'p-2 rounded-lg border transition-colors',
                isColumnSettingsOpen || hiddenColumns.length > 0
                  ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                  : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
              )}
            >
              <Settings className="w-4 h-4" />
            </button>
            {isColumnSettingsOpen && createPortal(
              <div
                ref={columnSettingsDropdownRef}
                className={clsx(
                  'fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl min-w-[220px] flex flex-col overflow-hidden',
                  isColumnSettingsPositioned ? 'animate-scaleIn' : 'opacity-0 pointer-events-none'
                )}
                style={isColumnSettingsPositioned
                  ? { top: columnSettingsPosition.top, left: columnSettingsPosition.left, maxHeight: `${window.innerHeight - columnSettingsPosition.top - 8}px` }
                  : { top: -9999, left: -9999 }
                }
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-1.5 text-xs font-medium text-dark-text-muted border-b border-dark-border">
                  Columns to display
                </div>
                <div className="flex-1 overflow-y-auto py-1">
                  {Object.entries(COLUMN_LABELS).map(([key, label]) => {
                    const hasFilter = columnHasActiveFilter(key);
                    const isVisible = !hiddenColumns.includes(key);
                    return (
                      <label
                        key={key}
                        className={clsx(
                          'flex items-center gap-2 px-3 py-1.5 text-sm transition-colors',
                          hasFilter
                            ? 'text-dark-text-muted cursor-not-allowed'
                            : 'text-dark-text-secondary hover:bg-dark-surface cursor-pointer'
                        )}
                        title={hasFilter ? 'Cannot hide column with active filter' : undefined}
                        onClick={(e) => {
                          if (hasFilter) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          disabled={hasFilter}
                          onChange={() => handleToggleColumnVisibility(key)}
                          className="accent-primary-500 w-3.5 h-3.5"
                        />
                        <span>{label}</span>
                        {hasFilter && (
                          <span className="ml-auto text-[10px] text-amber-400">filtered</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>,
              document.body
            )}

            {/* Top pagination controls */}
            {pagination && pagination.totalPages > 0 && (
              <div className="ml-auto">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  hasNext={pagination.hasNext}
                  hasPrev={pagination.hasPrev}
                  onPageChange={setPage}
                  isFetching={isFetching}
                  compact
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 min-h-0 h-full relative">
            <TableLoadingOverlay isVisible={isFetching && !isLoading} />
            {isLoading ? (
              <TableSkeleton rows={8} />
            ) : (
              <Table
                data={concertRows}
                columns={visibleColumns}
                searchable={false}
                emptyMessage={
                  dateMode === 'upcoming'
                    ? 'No upcoming concerts'
                    : dateMode === 'past'
                      ? 'No past concerts'
                      : 'No concerts found'
                }
                serverSort={true}
                sortConfig={tableSortConfig}
                onSort={setSort}
                rowContextMenu={getRowContextMenu}
                rowClassName={(row) => row.status === 'canceled' ? 'opacity-50' : undefined}
              />
            )}
          </div>

          {/* Bottom pagination */}
          {pagination && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              pageSize={pagination.pageSize}
              hasNext={pagination.hasNext}
              hasPrev={pagination.hasPrev}
              onPageChange={setPage}
              isFetching={isFetching}
              hideShowingText
            />
          )}
        </div>
      </div>

      <ConcertModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingConcert(null); }}
        onSubmit={modalMode === 'edit' ? handleUpdate : handleCreate}
        onDelete={modalMode === 'edit' ? handleDelete : undefined}
        concert={editingConcert}
        mode={modalMode}
        bands={bands}
        defaultVenueName={editingConcert ? venueMap.get(editingConcert.venue_id)?.name : undefined}
        onOpenVenueContact={(venueId) => { void openVenueContacts(venueId); }}
      />

      <ContactOverlay
        isOpen={isContactOverlayOpen}
        onClose={() => {
          openRequest.cancel();
          setIsContactOverlayOpen(false);
          setSelectedVenue(null);
          setSelectedVenueContacts([]);
        }}
        venue={selectedVenue}
        contacts={selectedVenueContacts}
        bands={bands}
        onRefreshContacts={() => { void refreshOverlayVenue(); }}
        onRefreshVenues={() => { void refreshOverlayVenue(); }}
      />

      <TodoModal
        isOpen={isTodoModalOpen}
        onClose={() => {
          todoVenueIdRef.current = null;
          setIsTodoModalOpen(false);
          setTodoVenue(null);
          setTodoConcertRow(null);
          setTodoContacts([]);
        }}
        onSubmit={handleCreateTodo}
        venueName={todoVenue?.name}
        contacts={todoContacts}
        bands={bands}
        initialValues={todoConcertRow ? {
          type: 'task',
          description: `Concert ${todoConcertRow.date ? format(new Date(todoConcertRow.date), 'dd MMM yyyy') : ''}: `,
          band_ids: [todoConcertRow.band_id],
        } : undefined}
      />

      <SaveViewModal
        isOpen={isSaveViewModalOpen}
        onClose={() => setIsSaveViewModalOpen(false)}
        onSubmit={handleSaveView}
        onUpdate={handleUpdateViewFilters}
        currentFilters={getCurrentFilters()}
        existingViews={namedViews}
      />

      <EditViewModal
        isOpen={!!editingView}
        onClose={() => setEditingView(null)}
        view={editingView}
        onRename={handleRenameView}
        onDelete={handleDeleteView}
      />
    </div>
  );
}
